<template>
  <div>
    <v-card class="mb-3 px-5 pt-4 pb-8 rounded-xl" :elevation="0">
      <div class="comments-container">
        <!-- Note message -->
        <div class="recording-message">
          <p class="grey--text text--lighten">
            Note: {{ recordDetails.message }}
          </p>
        </div>

        <!-- Comments section -->
        <div class="mb-4">
          <!-- Add new comment form -->
          <div class="new-comment-form" v-if="showNewCommentForm">
            <div class="new-comment-content">
              <div class="new-comment-input-wrapper">
                <v-textarea
                  solo
                  outlined
                  rows="1"
                  class="new-comment-textarea"
                  label="Leave a comment..."
                  ref="newCommentInput"
                  v-model="newCommentText"
                  :rules="requiredRules"
                  style="border-radius: 8px"
                  @input="autoResize($event)"
                  @keydown.enter.prevent="addComment"
                ></v-textarea>

                <div class="new-comment-form-actions mt-2">
                  <button class="form-btn cancel-btn" @click="cancelNewComment">
                    Cancel
                  </button>
                  <button
                    class="form-btn reply-submit-btn"
                    @click="addComment"
                    :loading="loading"
                    :disabled="!newCommentText.trim()"
                  >
                    Comment at 0:00
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Add comment button -->
          <div class="add-comment-trigger" v-if="!showNewCommentForm">
            <button class="add-comment-btn" @click="showNewCommentForm = true">
              <v-avatar size="32" class="mr-2">
                <v-icon size="20" color="grey darken-1">mdi-account</v-icon>
              </v-avatar>
              <span class="add-comment-text">Leave a comment</span>
            </button>
          </div>
        </div>

        <!-- Comments header -->
        <div class="comments-header">
          <span class="comments-count"
            >{{ comments.length }} comment{{
              comments.length !== 1 ? "s" : ""
            }}</span
          >
        </div>

        <!-- Comments list -->
        <div class="comments-list">
          <div
            v-for="comment in comments"
            :key="comment.id"
            class="comment-wrapper"
          >
            <!-- Main comment -->
            <div
              class="comment-item"
              @mouseenter="hoveredComment = comment.id"
              @mouseleave="hoveredComment = null"
            >
              <div class="comment-content">
                <div class="comment-left">
                  <v-avatar size="32" class="comment-avatar">
                    <v-icon size="20" color="grey darken-1">mdi-account</v-icon>
                  </v-avatar>

                  <div class="comment-body">
                    <div class="comment-header">
                      <span class="username">{{ comment.username }}</span>
                      <span class="timestamp"
                        >·&nbsp;{{ timeAgo(comment.created_at) }}</span
                      >
                    </div>

                    <div class="comment-text" v-if="comment.content.trim()">
                      {{ comment.content }}
                    </div>

                    <div class="comment-actions">
                      <button
                        class="action-btn reply-btn"
                        @click="toggleReplyForm(comment.id)"
                      >
                        Reply
                      </button>

                      <button
                        class="action-btn emoji-btn"
                        title="Coming soon..."
                      >
                        <v-icon size="16">mdi-emoticon-happy-outline</v-icon>
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Hover actions -->
                <div
                  class="comment-actions-hover"
                  :class="{ visible: hoveredComment === comment.id }"
                >
                  <v-btn
                    icon
                    small
                    @click="editComment(comment.id)"
                    class="hover-action-btn"
                    title="Edit comment"
                  >
                    <v-icon size="18">mdi-pencil</v-icon>
                  </v-btn>

                  <v-btn
                    icon
                    small
                    @click="deleteComment(comment.id)"
                    class="hover-action-btn"
                    title="Delete comment"
                  >
                    <v-icon size="18">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Reply form -->
            <div v-if="showReplyForm === comment.id" class="reply-form">
              <div class="reply-form-content">
                <v-avatar size="28" class="reply-avatar">
                  <v-icon size="16" color="grey darken-1">mdi-account</v-icon>
                </v-avatar>

                <div class="reply-input-wrapper">
                  <div class="reply-input-container">
                    <textarea
                      :ref="'replyInput-' + comment.id"
                      v-model="replyText"
                      :placeholder="
                        isEditing ? 'Edit comment' : 'Leave a comment'
                      "
                      rows="1"
                      class="reply-textarea"
                      @input="autoResize"
                      @keydown.enter.prevent="
                        isEditing
                          ? updateComment(editingCommentId)
                          : addReply(comment.id)
                      "
                    ></textarea>
                  </div>

                  <div class="reply-form-actions">
                    <button class="form-btn cancel-btn" @click="cancelReply">
                      Cancel
                    </button>
                    <button
                      class="form-btn reply-submit-btn"
                      @click="
                        isEditing
                          ? updateComment(editingCommentId)
                          : addReply(comment.id)
                      "
                      :disabled="!replyText.trim()"
                    >
                      {{ isEditing ? "Update" : "Reply" }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Replies -->
            <div
              v-if="comment.replies && comment.replies.length > 0"
              class="replies-container"
            >
              <div
                v-for="reply in comment.replies"
                :key="reply.id"
                class="reply-item"
                @mouseenter="hoveredComment = reply.id"
                @mouseleave="hoveredComment = null"
              >
                <div class="reply-content">
                  <div class="reply-left">
                    <v-avatar size="28" class="reply-avatar">
                      <v-icon size="16" color="grey darken-1"
                        >mdi-account</v-icon
                      >
                    </v-avatar>

                    <div class="reply-body">
                      <div class="reply-header">
                        <span class="username">{{ reply.username }}</span>
                        <span class="timestamp"
                          >·&nbsp;{{ timeAgo(reply.created_at) }}</span
                        >
                      </div>

                      <div class="reply-text" v-if="reply.content.trim()">
                        {{ reply.content }}
                      </div>
                    </div>
                  </div>

                  <!-- Reply hover actions -->
                  <div
                    class="comment-actions-hover"
                    :class="{ visible: hoveredComment === reply.id }"
                  >
                    <v-btn
                      icon
                      small
                      @click="editComment(reply.id)"
                      class="hover-action-btn"
                      title="Edit comment"
                    >
                      <v-icon size="16">mdi-pencil</v-icon>
                    </v-btn>

                    <v-btn
                      icon
                      small
                      @click="deleteComment(reply.id)"
                      class="hover-action-btn"
                      title="Delete comment"
                    >
                      <v-icon size="16">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>
<script>
import { mapActions, mapMutations } from "vuex";

export default {
  name: "Activities",
  props: {
    recordDetails: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      form: {},
      loading: false,
      comments: [],
      newCommentText: "",
      replyText: "",
      showReplyForm: null,
      showNewCommentForm: false,
      hoveredComment: null,
      isEditing: false,
      editingCommentId: null,
      originalCommentText: "",
      requiredRules: [(v) => !!v || "Comment cannot be empty"],
    };
  },
  methods: {
    ...mapMutations("comments", ["SHOW_DETAILS"]),
    ...mapActions({
      video_comments: "comments/video_comments",
      add_comment: "comments/add_comment",
    }),
    timeAgo(dateString) {
      const date = new Date(dateString);
      const seconds = Math.floor((new Date() - date) / 1000);

      if (seconds < 60) return `${seconds} sec ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} min ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hr ago`;
      const days = Math.floor(hours / 24);
      if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
      const years = Math.floor(months / 12);
      return `${years} year${years > 1 ? "s" : ""} ago`;
    },

    autoResize(event) {
      // Handle v-textarea (Vuetify) input
      if (event.target === undefined && typeof event === "string") {
        const textarea =
          this.$refs.newCommentInput.$el.querySelector("textarea");
        if (textarea) {
          textarea.style.height = "auto";
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
        return;
      }

      // Handle regular textarea input
      const textarea = event.target;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    },

    toggleReplyForm(commentId) {
      if (this.showReplyForm === commentId) {
        this.showReplyForm = null;
        this.replyText = "";
      } else {
        this.showReplyForm = commentId;
        this.replyText = "";
        this.$nextTick(() => {
          const replyInput = this.$refs[`replyInput-${commentId}`];
          if (replyInput && replyInput[0]) {
            replyInput[0].focus();
          }
        });
      }
    },

    cancelReply() {
      this.showReplyForm = null;
      this.replyText = "";
      this.isEditing = false;
      this.editingCommentId = null;
      this.originalCommentText = "";
    },

    cancelNewComment() {
      this.newCommentText = "";
      this.showNewCommentForm = false;
    },

    async addComment() {
      if (!this.newCommentText.trim()) return;

      this.loading = true;

      const newComment = {
        user_id: this.recordDetails.user.id,
        recording_id: this.recordDetails.id,
        username: this.recordDetails?.user?.name,
        content: this.newCommentText.trim(),
        created_at: new Date(),
        replies: [],
      };

      try {
        // Save to backend first and get the response with actual ID
        const response = await this.saveComment(newComment);

        if (response && response.data) {
          // Add the comment with backend data to frontend
          const commentFromBackend = {
            id: response.data.id, // Use actual backend ID
            user_id: response.data.user_id,
            parent_id: response.data.parent_id,
            recording_id: response.data.recording_id,
            username:
              response.data.user?.name || this.recordDetails?.user?.name,
            content: response.data.content,
            created_at: response.data.created_at,
            replies: [],
          };

          this.comments.unshift(commentFromBackend);
          console.log("Comment added successfully");
        }
      } catch (error) {
        console.error("Error adding comment:", error);
      } finally {
        this.newCommentText = "";
        this.showNewCommentForm = false;
        this.loading = false;
      }
    },

    async addReply(parentId) {
      if (!this.replyText.trim()) return;

      this.loading = true;

      const newReply = {
        parent_id: parentId,
        user_id: this.recordDetails.user.id,
        recording_id: this.recordDetails.id,
        username: this.recordDetails?.user?.name,
        content: this.replyText.trim(),
        created_at: new Date(),
      };

      try {
        // Save to backend first and get the response with actual ID
        const response = await this.saveComment(newReply);

        if (response && response.data) {
          const replyFromBackend = {
            id: response.data.id, // Use actual backend ID
            parent_id: response.data.parent_id,
            user_id: response.data.user_id,
            recording_id: response.data.recording_id,
            username:
              response.data.user?.name || this.recordDetails?.user?.name,
            content: response.data.content,
            created_at: response.data.created_at,
          };

          const parentComment = this.comments.find((c) => c.id === parentId);
          if (parentComment) {
            if (!parentComment.replies) {
              this.$set(parentComment, "replies", []);
            }
            parentComment.replies.push(replyFromBackend);
          }

          console.log("Reply added successfully");
        }
      } catch (error) {
        console.error("Error adding reply:", error);
      } finally {
        this.replyText = "";
        this.showReplyForm = null;
        this.loading = false;
      }
    },

    async saveComment(comment) {
      this.form = {
        user_id: comment.user_id,
        parent_id: comment.parent_id,
        recording_id: comment.recording_id,
        comment: comment.content,
        commented_at: 0, // commented at 0:00 (in seconds)
      };

      try {
        const response = await this.add_comment(this.form);
        console.log("Comment saved:", response.data);
        return response; // Return the response so we can use the backend data
      } catch (error) {
        console.error("Error saving comment:", error);
        throw error; // Re-throw so calling methods can handle it
      }
    },

    editComment(commentId) {
      // Find the comment to edit (could be main comment or reply)
      let commentToEdit = null;

      // Search in main comments
      commentToEdit = this.comments.find((c) => c.id === commentId);

      // If not found, search in replies
      if (!commentToEdit) {
        for (const comment of this.comments) {
          if (comment.replies) {
            commentToEdit = comment.replies.find((r) => r.id === commentId);
            if (commentToEdit) break;
          }
        }
      }

      if (commentToEdit) {
        this.isEditing = true;
        this.editingCommentId = commentId;
        this.originalCommentText = commentToEdit.content;
        this.replyText = commentToEdit.content;
        this.showReplyForm = commentToEdit.parent_id || commentId;
      }
    },

    async deleteComment(commentId) {
      const confirmed = window.confirm(
        "Are you sure you want to delete this comment?"
      );
      if (!confirmed) return;

      try {
        await this.$store.dispatch("comments/delete_comment", {
          id: commentId,
        });

        // Remove comment from state
        const commentIndex = this.comments.findIndex((c) => c.id === commentId);
        if (commentIndex > -1) {
          this.comments.splice(commentIndex, 1);
        } else {
          // Look for comment in replies
          for (const comment of this.comments) {
            if (comment.replies) {
              const replyIndex = comment.replies.findIndex(
                (r) => r.id === commentId
              );
              if (replyIndex > -1) {
                comment.replies.splice(replyIndex, 1);
                break;
              }
            }
          }
        }
      } catch (error) {
        console.error("Error deleting comment:", error);
      }
    },

    // generateId() {
    //   return Math.floor(Math.random() * 10000) + Date.now();
    // },

    async getVideoComments() {
      try {
        const response = await this.video_comments(this.recordDetails.id);
        if (response.success) {
          this.comments = response.data;
        }
      } catch (e) {
        console.log(e);
        this.comments = [];
      }
    },

    async updateComment(commentId) {
      if (!this.replyText.trim()) return;

      let commentToUpdate = null;
      let commentIndex = this.comments.findIndex((c) => c.id === commentId);
      let parentComment = null;
      let replyIndex = -1;

      try {
        // Find the comment or reply
        if (commentIndex > -1) {
          commentToUpdate = this.comments[commentIndex];
        } else {
          // Search in replies
          for (const comment of this.comments) {
            if (comment.replies) {
              replyIndex = comment.replies.findIndex((r) => r.id === commentId);
              if (replyIndex > -1) {
                commentToUpdate = comment.replies[replyIndex];
                parentComment = comment;
                break;
              }
            }
          }
        }

        const response = await this.$store.dispatch("comments/update_comment", {
          id: commentId,
          comment: this.replyText.trim(),
          commented_at: 0,
        });

        if (response.success) {
          // Update the UI after successful API call - FIX: Use 'content' instead of 'comment'
          if (commentIndex > -1) {
            this.$set(this.comments, commentIndex, {
              ...commentToUpdate,
              content: this.replyText.trim(), // Changed from 'comment' to 'content'
            });
          } else if (parentComment && replyIndex > -1) {
            this.$set(parentComment.replies, replyIndex, {
              ...commentToUpdate,
              content: this.replyText.trim(), // Changed from 'comment' to 'content'
            });
          }

          console.log("Comment updated successfully");
        } else {
          throw new Error("Failed to update comment");
        }
      } catch (error) {
        console.error("Error updating comment:", error);

        // Revert changes on error
        if (commentToUpdate) {
          commentToUpdate.content = this.originalCommentText; // Changed from 'comment' to 'content'
        }
      } finally {
        this.cancelReply();
      }
    },
  },
  mounted() {
    this.getVideoComments();
  },
};
</script>

<style lang="scss" scoped>
// global styles
.comments-container {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  max-width: 600px;
  margin: 0;
  padding: 0;
  text-align: left;
}

.comments-header {
  margin-bottom: 16px;

  .comments-count {
    color: #65676b;
    font-size: 17px;
    font-weight: 700;
    line-height: 20px;
  }
}

.comment-wrapper {
  margin-bottom: 8px;
}

.comment-item {
  position: relative;
  padding: 8px 4px;
  border-radius: 6px;

  &:hover {
    background-color: #f2f3f5;
    .comment-actions-hover {
      opacity: 1;
      visibility: visible;
    }
  }
}

.comment-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.comment-left {
  display: flex;
  flex: 1;
  align-items: flex-start;
}

.comment-avatar,
.reply-avatar {
  margin-right: 8px;
  flex-shrink: 0;
}

.comment-body,
.reply-body {
  flex: 1;
  min-width: 0;
}

.comment-header,
.reply-header {
  display: flex;
  align-items: baseline;
  margin-bottom: 2px;

  .username {
    color: #050505;
    font-size: 13px;
    font-weight: 600;
    line-height: 16px;
    margin-right: 6px;
  }

  .timestamp {
    color: #65676b;
    font-size: 12px;
    line-height: 16px;
    // cursor: pointer;

    // &:hover {
    //   text-decoration: underline;
    // }
  }
}

.comment-text,
.reply-text {
  color: #050505;
  font-size: 15px;
  line-height: 20px;
  margin-bottom: 4px;
  word-break: break-word;
}

.comment-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.action-btn {
  background: none;
  border: none;
  color: #65676b;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;

  // &:hover {}

  &.reply-btn:hover {
    text-decoration: underline;
    color: #1877f2;
  }

  // &.emoji-btn:hover {}
}

.comment-actions-hover {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
}

.hover-action-btn {
  background-color: #f2f3f5;
  border-radius: 6px;
  min-width: 32px !important;
  width: 32px;
  height: 32px;

  &:hover {
    background-color: #e4e6ea !important;
  }
}

.replies-container {
  margin-left: 40px;
  margin-top: 8px;
  border-left: none;
}

.reply-item {
  margin-bottom: 8px;
  position: relative;
  border-radius: 6px;

  &:hover {
    background-color: #f2f3f5;
    .comment-actions-hover {
      opacity: 1;
      visibility: visible;
    }
  }
}

.reply-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 4px 0;
}

.reply-left {
  display: flex;
  flex: 1;
  align-items: flex-start;
}

.nested-reply-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 36px;
  margin-top: 8px;
  margin-bottom: 8px;
}

// Reply form styles
.reply-form,
.new-comment-form {
  margin-top: 8px;
  margin-left: 40px;
}

.new-comment-form {
  margin-left: 0;
  margin-top: 16px;
}

.reply-form-content,
.new-comment-content {
  display: flex;
  align-items: flex-start;
}

.reply-input-wrapper,
.new-comment-input-wrapper {
  flex: 1;
}

// .new-comment-input-container {
.reply-input-container {
  border: 1px solid #1877f2;
  border-radius: 20px;
  padding: 8px 12px;
  background: #f0f2f5;
  margin-bottom: 8px;
}

.new-comment-textarea {
  ::v-deep .v-input__control {
    .v-input__slot {
      margin-bottom: 0 !important;
    }
    .v-text-field__details {
      display: none;
    }
  }
}
.reply-textarea {
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-family: inherit;
  font-size: 15px;
  line-height: 20px;
  color: #050505;
  min-height: 20px;

  &::placeholder {
    color: #65676b;
  }
}

.reply-form-actions,
.new-comment-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.form-btn {
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  padding: 6px 12px;

  &.cancel-btn {
    background: none;
    color: #65676b;

    &:hover {
      background-color: #f2f3f5;
    }
  }

  &.reply-submit-btn {
    background-color: #1877f2;
    color: white;

    &:hover:not(:disabled) {
      background-color: #166fe5;
    }

    &:disabled {
      background-color: #e4e6ea;
      color: #bcc0c4;
      cursor: not-allowed;
    }
  }
}

// Add comment trigger
.add-comment-trigger {
  margin-top: 16px;
}

.add-comment-btn {
  display: flex;
  align-items: center;
  width: 100%;
  background: #f0f2f5;
  border: none;
  border-radius: 20px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e4e6ea;
  }
}

.add-comment-text {
  color: #65676b;
  font-size: 15px;
  line-height: 20px;
}

// Responsive
@media (max-width: 600px) {
  .replies-container {
    margin-left: 28px;
  }

  .reply-form,
  .new-comment-form {
    margin-left: 28px;
  }

  .nested-reply-actions {
    margin-left: 28px;
  }
}
</style>
